"use client";

import { c as _c } from "react/compiler-runtime";
import { ColumnDef, Row } from "@tanstack/react-table";
import { TestTube } from "lucide-react";
import { useLabResultsColumnFilters } from "@/src/components/cards/lab-results-card/lab-results-columns";
import { LabResultsToolbar } from "@/src/components/cards/lab-results-card/lab-results-toolbar";
import { CopyableContent } from "@/src/components/copyable-content";
import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { DataTable } from "@/src/components/tables/data-table";
import { DataTableColumnHeader } from "@/src/components/tables/data-table-column-header";
import { EnabledFields } from "@/src/lib/schema-types";
import { getClassificationDisplay } from "@/src/lib/utils/classification";
import { formatDate } from "@/src/lib/utils/date-and-phone-utils";
import { isFieldEnabled, hasValue } from "@/src/lib/utils/object-utils";
import { Classification } from "@/src/models/constants/classifications";
import { LabResults } from "@/src/models/documents/lab-results/lab-results";
interface LabResultsWithClassification extends LabResults {
  classification: Classification;
}
interface LabResultsTableProps {
  labResults: (LabResults & {
    classification: Classification;
  })[];
  enabledFields?: EnabledFields;
}
export function LabResultsTable(t0) {
  const $ = _c(14);
  const {
    labResults,
    enabledFields
  } = t0;
  const [columnFilters] = useLabResultsColumnFilters();
  let t1;
  if ($[0] !== columnFilters.labResultsClassification || $[1] !== columnFilters.needsSignature || $[2] !== columnFilters.typeOfLab) {
    t1 = {
      typeOfLab: columnFilters.typeOfLab,
      needsSignature: columnFilters.needsSignature,
      classification: columnFilters.labResultsClassification
    };
    $[0] = columnFilters.labResultsClassification;
    $[1] = columnFilters.needsSignature;
    $[2] = columnFilters.typeOfLab;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const mappedColumnFilters = t1;
  const dataWithClassification = labResults;
  const CellWrapper = _temp;
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = _temp2;
    t3 = t4 => {
      const {
        row
      } = t4;
      const value = row.getValue("classification") as Classification;
      const displayValue = getClassificationDisplay(value);
      return <CellWrapper><span className="no-select"><CopyableContent text={displayValue} /></span></CellWrapper>;
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== dataWithClassification || $[7] !== enabledFields) {
    const allColumns = [{
      accessorKey: "classification",
      header: t2,
      cell: t3,
      filterFn: "arrIncludesSome"
    }, {
      accessorKey: "nameOfLab",
      header: _temp3,
      cell: t5 => {
        const {
          row: row_0
        } = t5;
        const value_0 = row_0.getValue("nameOfLab") as string;
        return <CellWrapper><span className="max-w-[200px] break-words font-medium no-select"><CopyableContent text={value_0 || "\u2014"} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "labTypesAndResults",
      header: _temp4,
      cell: t6 => {
        const {
          row: row_1
        } = t6;
        const value_1 = row_1.getValue("labTypesAndResults") as Array<{
          typeOfLab: string;
          labResult: string;
        }> | null;
        if (!value_1 || value_1.length === 0) {
          return <CellWrapper><span className="no-select">—</span></CellWrapper>;
        }
        return <CellWrapper><div className="max-w-[500px] no-select"><ul className="list-disc list-inside space-y-1">{value_1.map(_temp5)}</ul></div></CellWrapper>;
      }
    }, {
      accessorKey: "collectionDate",
      header: _temp6,
      cell: t7 => {
        const {
          row: row_2
        } = t7;
        const value_2 = row_2.getValue("collectionDate") as string;
        const displayValue_0 = value_2 ? formatDate(value_2) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_0} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "dateOfLabResults",
      header: _temp7,
      cell: t8 => {
        const {
          row: row_3
        } = t8;
        const value_3 = row_3.getValue("dateOfLabResults") as string;
        const displayValue_1 = value_3 ? formatDate(value_3) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_1} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "orderingProvider",
      header: _temp8,
      cell: t9 => {
        const {
          row: row_4
        } = t9;
        const provider = row_4.getValue("orderingProvider") as any;
        const displayValue_2 = provider && provider.givenName && provider.familyName ? `${provider.givenName} ${provider.familyName}` : "\u2014";
        const npiValue = provider?.npiNumber || null;
        return <CellWrapper><div className="max-w-[500px] truncate font-medium no-select"><div><CopyableContent text={displayValue_2} /></div>{npiValue && <div className="text-sm text-muted-foreground mt-1">NPI: <CopyableContent text={npiValue} /></div>}</div></CellWrapper>;
      },
      filterFn: "arrIncludesSome",
      accessorFn: _temp9
    }, {
      accessorKey: "listOfNameOfLabs",
      header: _temp10,
      cell: t10 => {
        const {
          row: row_6
        } = t10;
        const value_4 = row_6.getValue("listOfNameOfLabs") as string[];
        const displayValue_3 = value_4 && value_4.length > 0 ? value_4.join(", ") : "\u2014";
        return <CellWrapper><span className="max-w-[200px] break-words font-medium no-select"><CopyableContent text={displayValue_3} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "bodyPart",
      header: _temp11,
      cell: t11 => {
        const {
          row: row_7
        } = t11;
        const value_5 = row_7.getValue("bodyPart") as string;
        return <CellWrapper><span className="no-select"><CopyableContent text={value_5 || "\u2014"} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "needsSignature",
      header: _temp12,
      cell: t12 => {
        const {
          row: row_8
        } = t12;
        const value_6 = row_8.getValue("needsSignature") as boolean;
        const displayValue_4 = value_6 !== null && value_6 !== undefined ? value_6 ? "Yes" : "No" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_4} /></span></CellWrapper>;
      },
      filterFn: _temp13
    }, {
      accessorKey: "dateOfEvent",
      header: _temp14,
      cell: t13 => {
        const {
          row: row_10
        } = t13;
        const value_8 = row_10.getValue("dateOfEvent") as string;
        const displayValue_5 = value_8 ? formatDate(value_8) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_5} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "dateOfDocument",
      header: _temp15,
      cell: t14 => {
        const {
          row: row_11
        } = t14;
        const value_9 = row_11.getValue("dateOfDocument") as string;
        const displayValue_6 = value_9 ? formatDate(value_9) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_6} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "dateReceived",
      header: _temp16,
      cell: t15 => {
        const {
          row: row_12
        } = t15;
        const value_10 = row_12.getValue("dateReceived") as string;
        const displayValue_7 = value_10 ? formatDate(value_10) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_7} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "dateSigned",
      header: _temp17,
      cell: t16 => {
        const {
          row: row_13
        } = t16;
        const value_11 = row_13.getValue("dateSigned") as string;
        const displayValue_8 = value_11 ? formatDate(value_11) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_8} /></span></CellWrapper>;
      }
    }];
    t4 = allColumns.filter(column_12 => {
      const fieldName = "accessorKey" in column_12 && typeof column_12.accessorKey === "string" ? column_12.accessorKey : "";
      if (fieldName === "classification") {
        return true;
      }
      return isFieldEnabled(enabledFields, fieldName) && dataWithClassification.some(item_0 => hasValue(item_0[fieldName as keyof LabResultsWithClassification]));
    });
    $[6] = dataWithClassification;
    $[7] = enabledFields;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const columns = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-md font-semibold flex items-center gap-2"><TestTube className="h-6 w-6" />Lab Results</CardTitle></CardHeader>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== columns || $[11] !== dataWithClassification || $[12] !== mappedColumnFilters) {
    t6 = <Card className="w-full">{t5}<CardContent><div className="rounded-lg overflow-hidden border"><DataTable columns={columns} data={dataWithClassification} columnFilters={mappedColumnFilters} ToolbarComponent={LabResultsToolbar} getRowUrl={_temp18} /></div></CardContent></Card>;
    $[10] = columns;
    $[11] = dataWithClassification;
    $[12] = mappedColumnFilters;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp18() {
  return "";
}
function _temp17(t0) {
  const {
    column: column_11
  } = t0;
  return <DataTableColumnHeader column={column_11} title="Signed Date" />;
}
function _temp16(t0) {
  const {
    column: column_10
  } = t0;
  return <DataTableColumnHeader column={column_10} title="Received Date" />;
}
function _temp15(t0) {
  const {
    column: column_9
  } = t0;
  return <DataTableColumnHeader column={column_9} title="Document Date" />;
}
function _temp14(t0) {
  const {
    column: column_8
  } = t0;
  return <DataTableColumnHeader column={column_8} title="Event Date" />;
}
function _temp13(row_9, id, value_7) {
  const rowValue = row_9.getValue(id) as boolean;
  return value_7.includes(rowValue ? "true" : "false");
}
function _temp12(t0) {
  const {
    column: column_7
  } = t0;
  return <DataTableColumnHeader column={column_7} title="Signature" />;
}
function _temp11(t0) {
  const {
    column: column_6
  } = t0;
  return <DataTableColumnHeader column={column_6} title="Body Part" />;
}
function _temp10(t0) {
  const {
    column: column_5
  } = t0;
  return <DataTableColumnHeader column={column_5} title="Lab List" />;
}
function _temp9(row_5) {
  const provider_0 = row_5.orderingProvider;
  return provider_0;
}
function _temp8(t0) {
  const {
    column: column_4
  } = t0;
  return <DataTableColumnHeader column={column_4} title="Provider" />;
}
function _temp7(t0) {
  const {
    column: column_3
  } = t0;
  return <DataTableColumnHeader column={column_3} title="Lab Date" />;
}
function _temp6(t0) {
  const {
    column: column_2
  } = t0;
  return <DataTableColumnHeader column={column_2} title="Collection Date" />;
}
function _temp5(item, index) {
  return <li key={index} className="text-sm"><CopyableContent text={`${item.typeOfLab || "N/A"}${item.labResult ? ` - ${item.labResult}` : ""}`} /></li>;
}
function _temp4(t0) {
  const {
    column: column_1
  } = t0;
  return <DataTableColumnHeader column={column_1} title="Type & Result" />;
}
function _temp3(t0) {
  const {
    column: column_0
  } = t0;
  return <DataTableColumnHeader column={column_0} title="Lab Name" />;
}
function _temp2(t0) {
  const {
    column
  } = t0;
  return <DataTableColumnHeader column={column} title="Classification" />;
}
function _temp(t0) {
  const {
    children
  } = t0;
  return <div className="w-full p-2">{children}</div>;
}